/**
 * Conversions between the two ways of framing a view.
 *
 * A perspective camera's apparent size comes from how far away it sits; an
 * orthographic one's comes from the size of its frustum. Switching between
 * them without the model appearing to jump means translating one into the
 * other, which is what these functions do.
 */

export type Projection = 'perspective' | 'orthographic';

export interface OrthoFrustum {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/** World-space height a perspective camera spans at the given distance. */
export function visibleHeightAtDistance(distance: number, fovDeg: number): number {
  return 2 * distance * Math.tan(((fovDeg * Math.PI) / 180) / 2);
}

/** Distance a perspective camera needs in order to span the given height. */
export function distanceForVisibleHeight(height: number, fovDeg: number): number {
  return height / (2 * Math.tan(((fovDeg * Math.PI) / 180) / 2));
}

/** Orthographic frustum spanning `height` vertically, widened by the aspect. */
export function orthoFrustum(height: number, aspect: number): OrthoFrustum {
  const halfHeight = height / 2;
  const halfWidth = halfHeight * aspect;
  return { left: -halfWidth, right: halfWidth, top: halfHeight, bottom: -halfHeight };
}
